import type React from "react"
import Link from "next/link"

import { SOFIA_DISPLAY_NAME } from "@/constants/sofia"
import {
  Button,
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@coval/ui"
import { Mail, Sparkles } from "lucide-react"

import { openCoviHome } from "@/lib/coviEvents"
import { buildSupportMailtoHref, SUPPORT_EMAIL } from "@/utils/supportEmail"

const SOFIA_HOME_BASE_HREF = "/home"

interface ContactSupportDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  /** Builds the org- and workspace-scoped href for an in-app path. */
  getHref: (baseHref: string) => string
}

/**
 * Where the account menu's Contact Us lands: Sofia first, since she answers
 * most product questions on the spot, and the support inbox for the rest.
 */
export function ContactSupportDialog({
  open,
  onOpenChange,
  getHref,
}: ContactSupportDialogProps) {
  // Like the sidebar's Sofia tab, a mounted CoviProvider owns the trip to Home
  // and resumes the engaged thread; with none answering, the link navigates.
  const handleAskSofia = (event: React.MouseEvent<HTMLAnchorElement>) => {
    const opensElsewhere =
      event.button !== 0 ||
      event.metaKey ||
      event.ctrlKey ||
      event.shiftKey ||
      event.altKey
    if (!opensElsewhere && openCoviHome()) {
      event.preventDefault()
    }
    onOpenChange(false)
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Contact us</DialogTitle>
          <DialogDescription>
            {SOFIA_DISPLAY_NAME} can answer most questions about using Coval
            immediately. For any other inquiries, email the Coval team.
          </DialogDescription>
        </DialogHeader>
        <DialogBody className="flex flex-col gap-2 sm:flex-row">
          <Button asChild className="flex-1">
            <Link href={getHref(SOFIA_HOME_BASE_HREF)} onClick={handleAskSofia}>
              <Sparkles className="size-4" aria-hidden />
              Ask {SOFIA_DISPLAY_NAME}
            </Link>
          </Button>
          <Button asChild variant="secondary" className="flex-1">
            <a href={buildSupportMailtoHref()}>
              <Mail className="size-4" aria-hidden />
              Email {SUPPORT_EMAIL}
            </a>
          </Button>
        </DialogBody>
      </DialogContent>
    </Dialog>
  )
}
